import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from "recharts";
import { 
  TrendingUp, DollarSign, Users, Calendar, Ticket, Heart, 
  ArrowUpRight, ArrowDownRight, RefreshCw
} from "lucide-react";
import { format } from "date-fns";

const COLORS = ['#1e3a5f', '#60a5fa', '#3b82f6', '#93c5fd', '#2563eb', '#1d4ed8'];

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  if (loading) {
    return (
      <AdminLayout currentPage="Analytics">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-slate-100 rounded-lg animate-pulse"></div>
            <div className="h-80 bg-slate-100 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="Analytics">
      <div className="p-8" data-testid="admin-analytics">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Analytics Dashboard</h1>
            <p className="text-slate-600">Track donations, supporters, and engagement</p>
          </div>
          <Button onClick={fetchAnalytics} variant="outline" data-testid="refresh-analytics">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-deep-blue to-deep-blue/80 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Donations</p>
                  <p className="text-3xl font-bold font-heading mt-1">
                    {formatCurrency(analytics?.totals?.donations || 0)}
                  </p>
                  <p className="text-awareness-blue text-sm mt-2 flex items-center">
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                    {analytics?.totals?.donation_count || 0} donations
                  </p>
                </div>
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-awareness-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Supporters</p>
                  <p className="text-3xl font-bold font-heading text-deep-blue mt-1">
                    {analytics?.totals?.supporters || 0}
                  </p>
                  <p className="text-green-600 text-sm mt-2 flex items-center">
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                    Growing community
                  </p>
                </div>
                <div className="w-14 h-14 bg-awareness-blue/10 rounded-full flex items-center justify-center">
                  <Users className="w-7 h-7 text-awareness-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Events</p>
                  <p className="text-3xl font-bold font-heading text-deep-blue mt-1">
                    {analytics?.totals?.events || 0}
                  </p>
                  <p className="text-slate-500 text-sm mt-2 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Events hosted
                  </p>
                </div>
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-7 h-7 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Tickets Sold</p>
                  <p className="text-3xl font-bold font-heading text-deep-blue mt-1">
                    {analytics?.totals?.tickets || 0}
                  </p>
                  <p className="text-slate-500 text-sm mt-2 flex items-center">
                    <Ticket className="w-4 h-4 mr-1" />
                    Event attendees
                  </p>
                </div>
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                  <Ticket className="w-7 h-7 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Donations Over Time */}
          <Card>
            <CardHeader>
              <CardTitle className="text-deep-blue">Donations Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80" data-testid="donations-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics?.monthly_data || []}>
                    <defs>
                      <linearGradient id="donationGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1e3a5f" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#1e3a5f" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), "Donations"]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="donations" 
                      stroke="#1e3a5f" 
                      strokeWidth={2}
                      fill="url(#donationGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Supporters Growth */}
          <Card>
            <CardHeader>
              <CardTitle className="text-deep-blue">Monthly Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80" data-testid="activity-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.monthly_data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Legend />
                    <Bar dataKey="donation_count" name="Donations" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="supporters" name="New Supporters" fill="#60a5fa" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="tickets" name="Tickets" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Campaigns */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-deep-blue">Campaign Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.campaigns && analytics.campaigns.length > 0 ? (
                <div className="space-y-4">
                  {analytics.campaigns.map((campaign, idx) => (
                    <div key={campaign.campaign_id} className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-deep-blue/10 rounded-full flex items-center justify-center text-deep-blue font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-deep-blue">{campaign.title}</span>
                          <span className="text-sm text-slate-600">{formatCurrency(campaign.raised || 0)}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div 
                            className="bg-awareness-blue h-2 rounded-full transition-all"
                            style={{ width: `${Math.min(((campaign.raised || 0) / (campaign.goal || 1)) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Goal: {formatCurrency(campaign.goal)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Heart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No campaigns yet</p>
                  <p className="text-sm">Create your first donation campaign</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Donations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-deep-blue">Recent Donations</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.recent_donations && analytics.recent_donations.length > 0 ? (
                <div className="space-y-3">
                  {analytics.recent_donations.slice(0, 5).map((donation) => (
                    <div key={donation.donation_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium text-deep-blue">
                          {donation.is_anonymous ? "Anonymous" : donation.donor_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {donation.created_at ? format(new Date(donation.created_at), "MMM d, yyyy") : ""}
                        </p>
                      </div>
                      <span className="font-bold text-awareness-blue">
                        {formatCurrency(donation.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No donations yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
